"use client";

import { sendSalesEvent } from "@/lib/analytics/sales-events";
import { usePathname, useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import { Suspense, useEffect } from "react";

/**
 * Inner listener that touches `useSearchParams()`.
 *
 * It must be isolated in its own Suspense boundary (see `AnalyticsProvider`)
 * because reading search params opts the surrounding subtree into client-side
 * rendering. Keeping it as a sibling that returns `null` means the page
 * `{children}` keep their full SSR/SSG output — which is what every web and
 * AI crawler reads. Without this isolation, the entire site bails out to
 * client-side rendering and crawlers see an empty `<body>` (no h1, no copy,
 * no structured data context).
 */
function AnalyticsListener() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const path = pathname ?? "/";
    const search = searchParams?.toString();
    const pageData = { pathname: path, search };
    void sendSalesEvent({ kind: "page_view", metadata: pageData });
  }, [pathname, searchParams]);

  useEffect(() => {
    const KEY = "mlx:web-session-started";
    if (sessionStorage.getItem(KEY)) return;
    sessionStorage.setItem(KEY, "1");
    void sendSalesEvent({ kind: "session_start" });
  }, []);

  return null;
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  return (
    <>
      <Suspense fallback={null}>
        <AnalyticsListener />
      </Suspense>
      {children}
    </>
  );
}
